import React from "react";
import { ChevronsUpDown, Plus, Star, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from "@/components/ui/drawer";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getOptimizedSeavienImageUrl, SMALL_THUMBNAIL_IMAGE_SIZE } from "@/main/util/ImageUrlHelper";
import type { Localizer } from "@/main/react/Types";
import { useMediaQueryMatches } from "@/main/react/hooks/useMediaQueryMatches";
import {
    fetchSubjectSuggestions,
    findNearMatch,
    halfStarsToStars,
    SUBJECT_SUGGESTION_DEBOUNCE_MS,
    SUBJECT_SUGGESTION_MIN_CHARS,
    type SubjectSuggestion,
} from "@/main/common/SubjectSuggestionService";
import {
    subjectSelectionFromSuggestion,
    type SubjectSelection,
} from "@/main/react/discussion/facet/ReviewFacetTypes";

type Phase = "idle" | "loading" | "ready" | "error";

const SUBJECT_COMBOBOX_DRAWER_MEDIA_QUERY = "(max-width: 640px)";

/**
 * Below this width the suggestion list is a bottom drawer rather than a popover: a popover anchored to
 * a full-width trigger has nowhere to go on a phone.
 *
 * <p>Delegates to `useMediaQueryMatches` (2026-08-15). This was a `useState` mirror re-published from a
 * mount effect, which is the shape `AGENTS.md` rule 31 forbids — the breakpoint decides which layout is
 * in the DOM, so it belongs on a `useSyncExternalStore` over the live `MediaQueryList` rather than on a
 * second copy of a value the browser already owns. Same first-render value, same `false` when
 * `matchMedia` is unavailable, same `change` subscription.
 */
function useIsMobileDrawerBreakpoint(): boolean {
    return useMediaQueryMatches(SUBJECT_COMBOBOX_DRAWER_MEDIA_QUERY);
}

function avatarFallback(name?: string | null): string {
    const trimmed = name?.trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : "?";
}

/**
 * One suggestion row's secondary line: `★4.4 · 12 reviews · Vehicles`.
 *
 * <p>The group names are the whole disambiguation mechanism (technical design §4.3): two subjects
 * called "Defender" are told apart by the groups their existing reviews were published into, which
 * is derived from real usage rather than declared by whoever created the subject first. They are
 * user-authored data and are therefore rendered verbatim, never translated.
 */
function SuggestionMeta({ suggestion, localizer }: { suggestion: SubjectSuggestion; localizer: Localizer }) {
    const stars = halfStarsToStars(suggestion.ratingAverageHalfStars);
    const ratingCount = suggestion.ratingCount ?? 0;
    const groupNames = (suggestion.groupNames ?? []).filter((name) => Boolean(name && name.trim()));

    const parts: React.ReactNode[] = [];
    if (stars != null && ratingCount > 0) {
        parts.push(
            <span key="rating" className="inline-flex items-center gap-0.5">
                <Star className="size-3 fill-primary text-primary" aria-hidden="true" />
                {stars}
            </span>,
        );
        parts.push(
            <span key="count">{localizer("discussion.facet.review.subject.reviews_count", ratingCount)}</span>,
        );
    } else {
        parts.push(<span key="none">{localizer("discussion.facet.review.subject.no_reviews_yet")}</span>);
    }
    groupNames.forEach((name, index) => parts.push(<span key={`group-${index}`}>{name}</span>));

    return (
        <span className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
            {parts.map((part, index) => (
                <React.Fragment key={index}>
                    {index > 0 ? <span aria-hidden="true">·</span> : null}
                    {part}
                </React.Fragment>
            ))}
        </span>
    );
}

function SuggestionRow({
    suggestion,
    localizer,
    onSelect,
    dataPlaywrightId,
}: {
    suggestion: SubjectSuggestion;
    localizer: Localizer;
    onSelect: () => void;
    dataPlaywrightId: string;
}) {
    const thumbnail = getOptimizedSeavienImageUrl(suggestion.imageUrl ?? undefined, SMALL_THUMBNAIL_IMAGE_SIZE) || undefined;
    return (
        <CommandItem
            value={suggestion.publicId}
            onSelect={onSelect}
            data-playwright-id={dataPlaywrightId}
            className="rounded-xl px-3 py-2.5"
        >
            <div className="flex min-w-0 flex-1 items-center gap-3">
                <Avatar size="sm" className="bg-muted">
                    <AvatarImage src={thumbnail} alt="" />
                    <AvatarFallback>{avatarFallback(suggestion.name)}</AvatarFallback>
                </Avatar>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate font-medium text-foreground">{suggestion.name}</span>
                    <SuggestionMeta suggestion={suggestion} localizer={localizer} />
                </span>
            </div>
        </CommandItem>
    );
}

export type SubjectComboboxProps = {
    localizer: Localizer;
    value: SubjectSelection | null;
    onChange: (selection: SubjectSelection | null) => void;
    /**
     * The group the author is publishing into. A RANKING signal only — the server never filters on
     * it (§5.3), so a subject with no group context stays reachable.
     */
    groupPublicId?: string;
    disabled?: boolean;
    /** Renders the label + help lines. Off inside a dialog that already carries them. */
    showLabel?: boolean;
    dataPlaywrightId?: string;
    className?: string;
    /**
     * Suggestion source. Defaults to the real endpoint; overridden only by the component showcase,
     * which has to render the loading / error / zero-result states without a backend.
     *
     * <p>A prop rather than a module mock so the showcase drives the SAME component the composer does
     * — a showcase that swapped in its own list would be showing a second implementation of the states
     * it claims to be checking.
     */
    fetchSuggestions?: typeof fetchSubjectSuggestions;
};

/**
 * The REVIEW facet's "what are you reviewing?" control: an async typeahead over subjects with an
 * inline create-on-the-fly item.
 *
 * <p>Structure from `DiscussionThoughtGroupFilter` (the tree's only cmdk consumer, and the one that
 * already solves the desktop-`Popover` / mobile-`Drawer` split); async behaviour from
 * `UsernameSuggestionField` (debounce + in-flight cancellation + an `idle | loading | ready | error`
 * phase machine). Neither precedent has create-on-the-fly, which is this component's own job.
 *
 * <p><b>Selecting *Create "…"* calls no endpoint.</b> It sets local state and the subject is created
 * server-side at draft-save/publish (§4.2/§4.5) — which keeps creation behind the publish path's
 * rate limits and means an abandoned draft never litters the subject table.
 *
 * <p><b>The "did you mean" row is the duplicate guard.</b> When the typed name near-matches an
 * existing subject, that subject is rendered a second time directly above the create item, both
 * selectable. This is what replaces a unique index on the subject name (decision 15): the *user*
 * resolves the ambiguity instead of the database silently resolving it for them. It fails silently
 * if it regresses, which is why it carries its own `data-playwright-id` and its own Jest assertion.
 */
export default function SubjectCombobox({
    localizer,
    value,
    onChange,
    groupPublicId,
    disabled = false,
    showLabel = true,
    dataPlaywrightId = "review-subject",
    className,
    fetchSuggestions = fetchSubjectSuggestions,
}: SubjectComboboxProps) {
    const isMobile = useIsMobileDrawerBreakpoint();
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const [phase, setPhase] = React.useState<Phase>("idle");
    const [suggestions, setSuggestions] = React.useState<SubjectSuggestion[]>([]);
    // Monotonic request id AND an AbortController: the id discards a late response that the
    // controller could not cancel in time, the controller stops the request costing anything.
    const requestIdRef = React.useRef(0);
    const abortRef = React.useRef<AbortController | null>(null);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        const trimmed = query.trim();
        if (trimmed.length < SUBJECT_SUGGESTION_MIN_CHARS) {
            abortRef.current?.abort();
            abortRef.current = null;
            requestIdRef.current += 1;
            setSuggestions([]);
            setPhase("idle");
            return;
        }

        const requestId = ++requestIdRef.current;
        setPhase("loading");

        const timeoutId = window.setTimeout(() => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            fetchSuggestions(trimmed, groupPublicId, controller.signal)
                .then((items) => {
                    if (requestIdRef.current !== requestId) {
                        return;
                    }
                    setSuggestions(items);
                    setPhase("ready");
                })
                .catch((error) => {
                    if (requestIdRef.current !== requestId) {
                        return;
                    }
                    // An abort is this component cancelling itself, not a failure the author caused —
                    // surfacing it as an error would flash "couldn't load" on every keystroke.
                    if (error instanceof DOMException && error.name === "AbortError") {
                        return;
                    }
                    setSuggestions([]);
                    setPhase("error");
                });
        }, SUBJECT_SUGGESTION_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [fetchSuggestions, groupPublicId, open, query]);

    React.useEffect(() => () => abortRef.current?.abort(), []);

    const trimmedQuery = query.trim();
    const hasEnoughCharacters = trimmedQuery.length >= SUBJECT_SUGGESTION_MIN_CHARS;
    const nearMatch = React.useMemo(
        () => (phase === "ready" ? findNearMatch(trimmedQuery, suggestions) : null),
        [phase, suggestions, trimmedQuery],
    );
    // No create item when the typed name IS one of the suggestions verbatim — offering to create a
    // duplicate of something already on screen is the exact mistake this control exists to prevent.
    const hasExactMatch = React.useMemo(
        () => suggestions.some((item) => item.name.trim().toLowerCase() === trimmedQuery.toLowerCase()),
        [suggestions, trimmedQuery],
    );
    const showCreateItem = hasEnoughCharacters && phase === "ready" && !hasExactMatch;

    const commit = React.useCallback((selection: SubjectSelection | null) => {
        onChange(selection);
        setOpen(false);
        setQuery("");
        setSuggestions([]);
        setPhase("idle");
    }, [onChange]);

    const handleOpenChange = React.useCallback((nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            setQuery("");
            setSuggestions([]);
            setPhase("idle");
        }
    }, []);

    const trigger = (
        <Button
            type="button"
            variant="outline"
            disabled={disabled}
            data-playwright-id={`${dataPlaywrightId}-trigger`}
            className={cn(
                "h-9 w-full justify-between rounded-full px-3 text-left font-normal",
                "[@media(pointer:coarse)]:min-h-11",
                value ? "text-foreground" : "text-muted-foreground",
            )}
        >
            <span className="min-w-0 truncate">
                {value ? value.name : localizer("discussion.facet.review.subject.placeholder")}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
    );

    const listBody = (
        <Command
            // Server-side matching: the endpoint already ranks across both derived name forms, so a
            // second client-side filter would only be able to REMOVE correct results.
            shouldFilter={false}
            data-playwright-id={`${dataPlaywrightId}-command`}
        >
            <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder={localizer("discussion.facet.review.subject.placeholder")}
                data-playwright-id={`${dataPlaywrightId}-input`}
            />
            <CommandList>
                {!hasEnoughCharacters ? (
                    <div className="px-4 py-6 text-sm text-muted-foreground" data-playwright-id={`${dataPlaywrightId}-min-chars`}>
                        {localizer("discussion.facet.review.subject.min_chars")}
                    </div>
                ) : null}

                {hasEnoughCharacters && phase === "loading" ? (
                    <div className="px-4 py-6 text-sm text-muted-foreground" data-playwright-id={`${dataPlaywrightId}-loading`}>
                        {localizer("discussion.facet.review.subject.searching")}
                    </div>
                ) : null}

                {hasEnoughCharacters && phase === "error" ? (
                    <div className="px-4 py-6 text-sm text-muted-foreground" data-playwright-id={`${dataPlaywrightId}-error`}>
                        {localizer("discussion.facet.review.subject.error")}
                    </div>
                ) : null}

                {/* cmdk's own empty slot stays out of the way: every empty state here is explicit,
                    because "nothing found" and "create this instead" are one decision for the user. */}
                <CommandEmpty className="hidden" />

                {phase === "ready" && suggestions.length > 0 ? (
                    <CommandGroup>
                        {suggestions.map((suggestion) => (
                            <SuggestionRow
                                key={suggestion.publicId}
                                suggestion={suggestion}
                                localizer={localizer}
                                onSelect={() => commit(subjectSelectionFromSuggestion(suggestion))}
                                dataPlaywrightId={`${dataPlaywrightId}-suggestion`}
                            />
                        ))}
                    </CommandGroup>
                ) : null}

                {phase === "ready" && suggestions.length === 0 && hasEnoughCharacters ? (
                    <div className="px-4 pt-6 pb-2 text-sm text-muted-foreground" data-playwright-id={`${dataPlaywrightId}-no-results`}>
                        {localizer("discussion.facet.review.subject.no_results")}
                    </div>
                ) : null}

                {showCreateItem ? (
                    <CommandGroup className="border-t border-border">
                        {/* Layer 2 of the dedupe design (§4.3): the near match, repeated directly
                            above the create item so the choice between "the existing one" and "a new
                            one" is made side by side rather than several rows apart. */}
                        {nearMatch ? (
                            <>
                                <div className="px-3 pt-2 text-xs font-medium text-muted-foreground">
                                    {localizer("discussion.facet.review.subject.did_you_mean")}
                                </div>
                                <SuggestionRow
                                    suggestion={nearMatch}
                                    localizer={localizer}
                                    onSelect={() => commit(subjectSelectionFromSuggestion(nearMatch))}
                                    dataPlaywrightId={`${dataPlaywrightId}-did-you-mean`}
                                />
                            </>
                        ) : null}
                        <CommandItem
                            value={`__create__${trimmedQuery}`}
                            onSelect={() => commit({ mode: "new", name: trimmedQuery })}
                            data-playwright-id={`${dataPlaywrightId}-create`}
                            className="rounded-xl px-3 py-2.5"
                        >
                            <span className="flex min-w-0 items-center gap-2">
                                <Plus className="size-4 shrink-0" aria-hidden="true" />
                                <span className="min-w-0 truncate">
                                    {localizer("discussion.facet.review.subject.create", trimmedQuery)}
                                </span>
                            </span>
                        </CommandItem>
                    </CommandGroup>
                ) : null}
            </CommandList>
        </Command>
    );

    return (
        <div className={cn("flex flex-col gap-1.5", className)} data-playwright-id={dataPlaywrightId}>
            {showLabel ? (
                <span className="text-sm font-medium text-foreground">
                    {localizer("discussion.facet.review.subject.label")}
                </span>
            ) : null}

            <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                    {isMobile ? (
                        <Drawer open={open} onOpenChange={handleOpenChange}>
                            <DrawerTrigger asChild>{trigger}</DrawerTrigger>
                            <DrawerContent className="max-h-[85vh]">
                                <DrawerHeader>
                                    <DrawerTitle>{localizer("discussion.facet.review.subject.label")}</DrawerTitle>
                                    <DrawerDescription>{localizer("discussion.facet.review.subject.help")}</DrawerDescription>
                                </DrawerHeader>
                                <div className="px-4 pb-4">{listBody}</div>
                            </DrawerContent>
                        </Drawer>
                    ) : (
                        <Popover open={open} onOpenChange={handleOpenChange}>
                            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
                            <PopoverContent align="start" className="w-[min(28rem,calc(100vw-2rem))] rounded-2xl p-2">
                                {listBody}
                            </PopoverContent>
                        </Popover>
                    )}
                </div>

                {value && !disabled ? (
                    <button
                        type="button"
                        onClick={() => onChange(null)}
                        data-playwright-id={`${dataPlaywrightId}-clear`}
                        className={cn(
                            "relative touch-hit-44 inline-flex shrink-0 items-center gap-1 rounded-full border border-border px-2 py-1 text-xs text-muted-foreground transition-colors",
                            "hover:bg-accent hover:text-foreground",
                            "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring",
                        )}
                    >
                        <X className="size-3.5" aria-hidden="true" />
                        {localizer("discussion.facet.review.subject.clear")}
                    </button>
                ) : null}
            </div>

            {showLabel ? (
                <span className="text-xs text-muted-foreground">
                    {localizer("discussion.facet.review.subject.help")}
                </span>
            ) : null}
        </div>
    );
}
